import { formatEuros } from "../meta/format.js";
import { planDiffers, shoppingList, type PlanRow, type ShoppingLine } from "./buildLogic.js";

/**
 * The workbench's one button, worked out away from the JSX: what it says, what
 * pressing it means, and why it is dead when it is. Four states, in the order
 * they gate — nothing to do, a part nobody sells, a shopping trip, the wrench.
 */

/** What the primary button would do if pressed. */
export type PrimaryAction = "none" | "buy" | "assemble";

export interface PrimaryButton {
  action: PrimaryAction;
  label: string;
  disabled: boolean;
  /** The hover sentence — the reason for a dead button, or what will happen. */
  title: string;
  /** What is still to be bought, so the caller need not ask twice. */
  lines: ShoppingLine[];
  /** Parts nobody in the valley sells; non-empty means the plan is a dead end. */
  unsellable: ShoppingLine[];
  /** Total asking price of `lines`, cheapest counter each. */
  cost: number;
}

export function primaryLabel(rows: readonly PlanRow[]): PrimaryButton {
  const lines = shoppingList(rows);
  const unsellable = lines.filter((line) => line.offer === null);
  const count = lines.reduce((sum, line) => sum + line.count, 0);
  const cost = lines.reduce((sum, line) => sum + (line.offer?.price ?? 0) * line.count, 0);

  if (!planDiffers(rows)) {
    return {
      action: "none",
      label: "Nothing to change",
      disabled: true,
      title: "The plan is the gun you already have.",
      lines,
      unsellable,
      cost,
    };
  }
  // A part off every shelf cannot be bought at any price, so the trip would be
  // pointless — the button names the first one and lists the rest on hover.
  if (unsellable.length > 0) {
    const first = unsellable[0];
    return {
      action: "none",
      label: `${first?.def.name ?? "A part"} is not for sale`,
      disabled: true,
      title: `Nobody in the valley sells ${unsellable.map((l) => l.def.name).join(", ")}.`,
      lines,
      unsellable,
      cost,
    };
  }
  if (count > 0) {
    return {
      action: "buy",
      label: `Buy ${count} missing part${count === 1 ? "" : "s"} · ${formatEuros(cost)}`,
      disabled: false,
      title: "Opens the market on exactly these parts.",
      lines,
      unsellable,
      cost,
    };
  }
  return {
    action: "assemble",
    label: "Assemble",
    disabled: false,
    title: "Fits every planned part from your stash and kit.",
    lines,
    unsellable,
    cost,
  };
}
